import InstitutionalCta from "../components/InstitutionalCta";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Cpu, FileBarChart, Bell, TrendingUp, Home, Scale, Building2, Briefcase } from "lucide-react";
import { api } from "../api";
import { useLang } from "../i18n";
import SearchWidget from "../components/SearchWidget";
import InstallCta from "../components/InstallCta";
import PolandMap from "../components/PolandMap";
import { StatusBadge, DemoRibbon } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const HERO_BG = "https://images.unsplash.com/photo-1686602700271-cd7af164205a?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function HomePage() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [gminas, setGminas] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/gminas").then((r) => setGminas(r.data.results)).catch(() => {});
    api.get("/tracker/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const steps = [
    { icon: Search, title: t("how.step1.title"), desc: t("how.step1.desc") },
    { icon: Cpu, title: t("how.step2.title"), desc: t("how.step2.desc") },
    { icon: FileBarChart, title: t("how.step3.title"), desc: t("how.step3.desc") },
  ];
  const audiences = [
    { icon: TrendingUp, title: t("audience.investors.title"), desc: t("audience.investors.desc") },
    { icon: Home, title: t("audience.owners.title"), desc: t("audience.owners.desc") },
    { icon: Briefcase, title: t("audience.agents.title"), desc: t("audience.agents.desc") },
    { icon: Scale, title: t("audience.lawyers.title"), desc: t("audience.lawyers.desc") },
    { icon: Building2, title: t("audience.municipalities.title"), desc: t("audience.municipalities.desc") },
  ];
  const faqs = [1, 2, 3, 4].map((i) => ({ q: t(`faq.q${i}`), a: t(`faq.a${i}`) }));

  return (
    <div>
      <section
        data-testid="hero-section"
        className="relative overflow-hidden bg-slate-900"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(9,13,22,0.94) 30%, rgba(9,13,22,0.55)), url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20">
          <div className="max-w-2xl">
            <span data-testid="hero-reform-badge" className="inline-flex items-center gap-2 rounded-full border border-[#DC143C]/50 bg-[#DC143C]/10 px-3 py-1 text-xs font-semibold text-red-300">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#EF4444]" />
              {t("hero.badge")}
            </span>
            <h1 className="mt-6 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
              {t("hero.subtitle")}
            </p>
            <p data-testid="hero-motto" className="mt-3 text-sm font-bold uppercase tracking-widest text-[#EF4444]">
              {t("hero.motto")}
            </p>
            <div className="mt-8">
              <p data-testid="hero-search-cta" className="mb-2 text-sm font-bold uppercase tracking-widest text-white/80">
                {t("search.cta")}
              </p>
              <SearchWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div data-testid="install-card" className="mt-8 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <div className="flex items-center gap-3">
            <img src="/pwa/icon-96.png" alt="" className="h-10 w-10 rounded-lg" />
            <div>
              <p className="text-sm font-bold text-slate-900">{t("install.card_title")}</p>
              <p className="text-sm text-slate-600">{t("install.card_text")}</p>
            </div>
          </div>
          <InstallCta testId="install-card-cta" buttonClass="text-xs" />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("how.title")}</h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {steps.map((s, i) => (
            <div key={i} data-testid={`how-step-${i + 1}`} className="rounded-xl border bg-white p-6 transition-shadow hover:shadow-md">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#FEF2F2] text-[#DC143C]">
                <s.icon size={20} />
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid gap-6 lg:grid-cols-2">
          <div data-testid="reports-teaser" className="rounded-xl border bg-slate-900 p-8 text-white">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#DC143C] text-white">
              <FileBarChart size={20} />
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold">{t("reports.title")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">{t("reports.subtitle")}</p>
            <div className="mt-5 rounded-lg border border-slate-700 bg-slate-800 p-4 font-mono-data text-xs text-slate-300">
              <div className="flex items-center justify-between">
                <p>RAPORT PLANISTYCZNY — Działka 182/4</p>
                <DemoRibbon label={t("demo.label")} />
              </div>
              <div className="mt-2 space-y-1 text-slate-400">
                <p>{t("reports.demo.line1")} · {t("reports.demo.line2")}</p>
                <p>{t("reports.demo.sources")}</p>
                <p>{t("reports.demo.updated")}</p>
                <p className="font-semibold text-amber-400">{t("reports.demo.status")}</p>
              </div>
            </div>
          </div>
          <div data-testid="watch-teaser" className="rounded-xl border bg-white p-8">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
              <Bell size={20} />
            </span>
            <h2 className="mt-4 font-heading text-2xl font-bold">{t("watch.title")}</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{t("watch.subtitle")}</p>
            <div data-testid="watch-demo-alert" className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-800">ALERT</p>
                <DemoRibbon label={t("demo.label")} />
              </div>
              <p className="mt-1.5 text-sm text-amber-900">{t("watch.demo.alert")}</p>
              <p className="mt-1 text-sm font-bold text-amber-900">{t("watch.demo.deadline")}</p>
              <button
                data-testid="watch-demo-action"
                onClick={() => navigate("/dzialka/146501_8.0702.182-4")}
                className="mt-2 text-sm font-semibold text-[#DC143C] hover:underline"
              >
                {t("watch.demo.action")}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="tracker-teaser" className="border-y bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("tracker.title")}</h2>
              <DemoRibbon label={t("demo.label")} />
            </div>
            <p className="mt-4 text-base text-slate-600">{t("tracker.subtitle")}</p>
            {stats && (
              <div data-testid="tracker-stats-cards" className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {["adopted", "draft", "consultation", "pending"].map((s) => (
                  <div key={s} className="rounded-lg border bg-slate-50 p-3 text-center">
                    <p className="font-heading text-2xl font-extrabold">{stats.by_status[s] || 0}</p>
                    <StatusBadge status={s} label={t(`status.${s}`)} testId={`stats-badge-${s}`} />
                  </div>
                ))}
              </div>
            )}
            <Button
              data-testid="tracker-open-button"
              className="mt-6 bg-slate-900 text-white hover:bg-slate-800"
              onClick={() => navigate("/tracker")}
            >
              {t("tracker.open")} →
            </Button>
          </div>
          <PolandMap gminas={gminas} />
        </div>
      </section>

      <section className="border-t bg-white">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
          <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("audience.title")}</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {audiences.map((a, i) => (
              <div key={i} data-testid={`audience-card-${i}`} className="rounded-xl border bg-slate-50 p-6 transition-colors hover:border-[#DC143C]/40">
                <a.icon size={22} className="text-[#DC143C]" />
                <h3 className="mt-3 font-heading text-base font-semibold">{a.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="cta-band" className="border-y bg-slate-900">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-5 px-4 py-12 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div>
            <h2 className="font-heading text-2xl font-bold text-white sm:text-3xl">{t("cta.band.title")}</h2>
            <p className="mt-1 text-sm font-semibold uppercase tracking-widest text-[#EF4444]">{t("hero.motto")}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              data-testid="cta-check-parcel-button"
              className="bg-[#DC143C] text-white hover:bg-[#B91C1C]"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            >
              {t("cta.check")}
            </Button>
            <Button
              data-testid="cta-generate-report-button"
              variant="outline"
              className="border-slate-600 bg-transparent text-white hover:bg-slate-800"
              onClick={() => navigate("/dzialka/146501_8.0702.182-4")}
            >
              {t("cta.report")}
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <h2 className="text-center font-heading text-2xl font-bold tracking-tight sm:text-3xl">{t("faq.title")}</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger data-testid={`faq-question-${i}`} className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm leading-relaxed text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section data-testid="institutional-section" className="border-t bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
          <InstitutionalCta variant="card" />
        </div>
      </section>

      <section data-testid="disclaimer-section" className="border-t bg-[#FEF2F2]">
        <div className="mx-auto max-w-4xl px-4 py-10 text-center sm:px-6">
          <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-[#991B1B]">{t("disclaimer.title")}</h3>
          <p className="mt-3 text-sm leading-relaxed text-slate-700">{t("disclaimer.text")}</p>
        </div>
      </section>
    </div>
  );
}
